// packages
import { useEffect, useMemo, useState } from 'react'
import { Baby, Check } from 'lucide-react'

// libs
import { cn } from '@/lib/utils'

// data
import { GEST_TRIMS } from './data'

// entities
import { signs_symptom_trimester, TrimesterChecks } from '@/entities/signs-symptom'

interface GestationalSectionProps {
  value: TrimesterChecks
  onChange: (next: TrimesterChecks) => void
  /** Trimestre inicialmente ativo (sugerido pelas semanas). Default = TRIM1. */
  initialTrim?: signs_symptom_trimester
}

const TRIM_ORDER: signs_symptom_trimester[] = [signs_symptom_trimester.TRIM1, signs_symptom_trimester.TRIM2, signs_symptom_trimester.TRIM3]

export function GestationalSection({ value, onChange, initialTrim }: GestationalSectionProps) {
  const [activeTrim, setActiveTrim] = useState<signs_symptom_trimester>(initialTrim ?? signs_symptom_trimester.TRIM1)
  const [userPickedTab, setUserPickedTab] = useState(false)

  useEffect(() => {
    if (!userPickedTab && initialTrim && initialTrim !== activeTrim) {
      setActiveTrim(initialTrim)
    }
  }, [initialTrim, userPickedTab])

  const activeDef = useMemo(() => GEST_TRIMS[activeTrim], [activeTrim])

  const toggle = (id: string) => {
    const trim = value[activeTrim] || {}
    const nextTrim: Record<string, boolean> = { ...trim, [id]: !trim[id] }
    onChange({ ...value, [activeTrim]: nextTrim })
  }

  const trimesterCount = (t: signs_symptom_trimester) => Object.values(value[t] || {}).filter(Boolean).length
  const totalCount = TRIM_ORDER.reduce((acc, t) => acc + trimesterCount(t), 0)

  return (
    <div className="rounded-2xl border border-border bg-card shadow-soft overflow-hidden">
      <div
        className="px-5 py-5 mdAndUp:px-6 mdAndUp:py-6"
        style={{ background: 'linear-gradient(135deg, hsl(var(--coentro)) 0%, hsl(var(--primary)) 100%)' }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center">
            <Baby className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg mdAndUp:text-xl font-semibold text-white">Sinais e sintomas da gestação</h2>
            <p className="text-sm text-white/85 mt-0.5">
              Selecione o trimestre e marque os sinais e sintomas presentes — cada um é um sinal de alerta a ser investigado
            </p>
          </div>
        </div>
      </div>

      <div className="p-5 mdAndUp:p-6">
        <div className="bg-secondary/60 rounded-xl p-1 flex items-center gap-1 mb-3 smAndDown:flex-wrap">
          {TRIM_ORDER.map(t => {
            const def = GEST_TRIMS[t]
            const isActive = t === activeTrim
            const count = trimesterCount(t)
            return (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setUserPickedTab(true)
                  setActiveTrim(t)
                }}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm transition-all smAndDown:min-w-[120px]',
                  isActive ? 'bg-card text-coentro font-semibold shadow-soft' : 'bg-transparent text-muted-foreground font-medium hover:text-foreground'
                )}
              >
                <span>{def.label}</span>
                {count > 0 && (
                  <span className={cn('inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold', isActive ? 'bg-coentro text-white' : 'bg-accent/40 text-jenipapo')}>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        <p className="text-xs text-muted-foreground mb-5 px-1">{activeDef.period}</p>

        <div className="grid grid-cols-1 xsAndUp:grid-cols-2 mdAndUp:grid-cols-3 lgAndUp:grid-cols-4 gap-2.5">
          {activeDef.syms.map(s => {
            const checked = !!value[activeTrim]?.[s.id]
            return (
              <button
                key={`${activeTrim}-${s.id}`}
                type="button"
                onClick={() => toggle(s.id)}
                className={cn(
                  'flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-all',
                  checked ? 'bg-primary/15 border-primary/40 ring-1 ring-primary/30' : 'bg-background border-border hover:border-primary/30 hover:bg-muted/40'
                )}
              >
                <span className={cn('flex-shrink-0 h-5 w-5 rounded-md border-2 flex items-center justify-center transition-colors', checked ? 'bg-coentro border-coentro' : 'bg-background border-border')}>
                  {checked && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                </span>
                <span className={cn('text-sm flex-1', checked ? 'text-foreground font-medium' : 'text-foreground/80')}>{s.name}</span>
              </button>
            )
          })}
        </div>

        {totalCount > 0 && (
          <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              <strong className="text-foreground font-semibold">{trimesterCount(activeTrim)}</strong> marcado{trimesterCount(activeTrim) !== 1 ? 's' : ''} neste trimestre
            </span>
            <span className="text-muted-foreground">
              <strong className="text-foreground font-semibold">{totalCount}</strong> sintoma{totalCount !== 1 ? 's' : ''} marcado{totalCount !== 1 ? 's' : ''} no total
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
